import { DayPoint, isoDateFor, lastIncludedLocalDay } from './ask-ai-person-signals.util';
import { joinWithAnd } from './ask-ai-signal-body';
import {
  CardSeverity, classifyBaseBand, FiredSignal, formatPersonSignalValue, personSignalTypeLabel, PersonSignalType,
  resolveSignalLink, SignalLinkBuilder, WEEKDAY_ORDER
} from './person-signal-card';

/**
 * AA-225 — View details, per user (week matrix). Pure data/logic, no Angular dependency, built
 * from AA-214's real FiredSignal[]/DayPoint[] data.
 *
 * Two things this module deliberately does NOT fabricate:
 *  - Per-user timezone: not available anywhere upstream, so no GMT header is rendered rather than
 *    mislabeling the employee's data with the viewing manager's zone.
 *  - Per-user work schedule: not available either, so `evaluated` below assumes global Mon-Fri
 *    (same as ask-ai-person-signals.util.ts), not each person's real days off.
 */

export interface PersonWeekDayValue {
  type: PersonSignalType;
  value: number; // native unit — percent for idle_time, decimal hours for the rest
  display: string; // formatted via formatPersonSignalValue
  over: boolean; // real breaching flag for this exact day (UserAlertEntry.severity !== 'ok')
  // Deep link for this day; always null on a non-evaluated day regardless of `over`.
  href: string | null;
}

export interface PersonWeekDay {
  date: string; // ISO yyyy-mm-dd, real — weekStart + this weekday's offset
  weekday: string; // 'Mon'..'Sun'
  // Mon-Fri only — see this file's own doc comment on why this is an assumption, not a fact.
  evaluated: boolean;
  // One entry per type with a real per-day value for this day, fired or not; absent means no data
  // at all — rendered as "no data", never guessed.
  values: PersonWeekDayValue[];
}

export interface PersonWeekEvidence {
  userId: string;
  // Same order/severities the card already computed — reused, not recomputed, so this screen can
  // never disagree with the card that linked to it.
  orderedTypes: PersonSignalType[];
  severities: Partial<Record<PersonSignalType, CardSeverity>>;
  days: PersonWeekDay[]; // elapsed days only, Mon..Sun order — today and future days are left out (AA-286)
  signals: FiredSignal[]; // exactly the card's fired types — real n/d/threshold/peak/peakDay
}

/**
 * Builds one user's week matrix from the same inputs used to build their PersonCard. `to`/`timezone`
 * matter because a week-to-date range (e.g. this week) must both mark a not-yet-happened weekday as
 * unevaluated (so weekAverage doesn't divide by all 5 Mon-Fri rows even for days that haven't
 * happened yet) and drop it from `days` entirely (AA-286) — rendering it would just be empty padding.
 */
export function buildPersonWeekEvidence(
  userId: string, weekStart: Date, orderedTypes: PersonSignalType[], severities: Partial<Record<PersonSignalType, CardSeverity>>,
  signals: FiredSignal[], dailyPointsByType: Partial<Record<PersonSignalType, DayPoint[]>>, buildLink: SignalLinkBuilder,
  to: string, timezone: string | undefined,
): PersonWeekEvidence {
  const types = Object.keys(dailyPointsByType) as PersonSignalType[];
  const lastIncludedDate = lastIncludedLocalDay(to, timezone).toISODate();

  const days: PersonWeekDay[] = WEEKDAY_ORDER
    .map((weekday, offset) => {
      const date = isoDateFor(weekStart, offset);
      // Mon-Fri, and not a day the range hasn't reached yet. An unparseable `to` (no lastIncludedDate)
      // falls back to the plain Mon-Fri rule instead of marking every day unevaluated.
      const evaluated = offset < 5 && (!lastIncludedDate || date <= lastIncludedDate);
      const dateObj = new Date(date);
      const values: PersonWeekDayValue[] = [];
      types.forEach(type => {
        const point = dailyPointsByType[type]?.find(p => p.day === weekday);
        if (!point) return;
        const sig = signals.find(s => s.type === type);
        const fallback = evaluated ? buildLink(type, userId, dateObj) : null;
        values.push({
          type, value: point.value, display: formatPersonSignalValue(type, point.value),
          over: point.breaching,
          // Prefer gateway deepLink when present; never overwrite it with the client-built URL.
          href: evaluated ? resolveSignalLink(sig ?? {}, fallback) : null,
        });
      });
      return { date, weekday, evaluated, values };
    })
    // Today and future days are omitted outright rather than rendered as empty "Not scheduled" rows
    // (AA-286) — a past weekend day stays (it's a real elapsed day, just never `evaluated`). An
    // unparseable `to` keeps every day, matching the `evaluated` fallback above.
    .filter(day => !lastIncludedDate || day.date <= lastIncludedDate);

  return { userId, orderedTypes, severities, days, signals };
}

/**
 * Matrix columns, in the same order as the card's own title/body (severity desc, then N desc — see
 * orderSignalsForCard). `heavy_hours` always renders as the trailing "Hours tracked" column, even
 * when it isn't one of the card's fired types (AA-251) — appended after the fired columns,
 * unthresholded, same convention as person-week-evidence-detail.ts's weekMatrixColumns.
 */
export function weekMatrixColumns(evidence: PersonWeekEvidence): PersonSignalType[] {
  if (evidence.orderedTypes.includes('heavy_hours')) return evidence.orderedTypes;
  return [...evidence.orderedTypes, 'heavy_hours'];
}

export function valueFor(day: PersonWeekDay, type: PersonSignalType): PersonWeekDayValue | undefined {
  return day.values.find(v => v.type === type);
}

/** True on every evaluated day whose real value ties the signal's peak, and only for n >= 2 (a
 * single flagged day is self-evidently the peak, so marking it is redundant). A tie (e.g. two days
 * both hitting the week's high) marks all of them, not just whichever the peak happened to anchor
 * on. Never true on a non-evaluated day. */
export function isPeakCell(day: PersonWeekDay, sig: FiredSignal): boolean {
  if (!day.evaluated || sig.n < 2) return false;
  const value = valueFor(day, sig.type);
  return value != null && value.over && value.value === sig.peak;
}

/** Every evaluated weekday tied for the signal's peak value, in week order. Falls back to
 * sig.peakDay when no real day-tagged value matches (the range-aggregate fallback: n=1, peakDay is
 * an estimated placeholder with no per-day values to compare against). */
function peakWeekdays(evidence: PersonWeekEvidence, sig: FiredSignal): string[] {
  const matches = evidence.days
    .filter(day => day.evaluated)
    .filter(day => {
      const value = valueFor(day, sig.type);
      return value != null && value.over && value.value === sig.peak;
    })
    .map(day => day.weekday);
  return matches.length ? matches : [sig.peakDay];
}

function evaluatedDays(evidence: PersonWeekEvidence): PersonWeekDay[] {
  return evidence.days.filter(d => d.evaluated);
}

/**
 * "N days evaluated, Mon to Fri." — evaluated days are always a contiguous Mon-first prefix (see
 * this file's own doc comment on the Mon-Fri assumption), so the range is just its first and last
 * weekday. Empty string when nothing was evaluated at all (no completed day yet).
 */
export function daysEvaluatedLine(evidence: PersonWeekEvidence): string {
  const days = evaluatedDays(evidence);
  if (!days.length) return '';
  const noun = days.length === 1 ? 'day' : 'days';
  const range = days.length === 1 ? days[0].weekday : `${days[0].weekday} to ${days[days.length - 1].weekday}`;
  return `${days.length} ${noun} evaluated, ${range}.`;
}

function rawValue(day: PersonWeekDay, type: PersonSignalType): number {
  return valueFor(day, type)?.value ?? 0;
}

/**
 * Average for one column over evaluated (Mon-Fri) days. idle_time excludes days with no value from
 * both sum and count; every other type treats a missing value as 0, so its denominator matches the
 * card's own `D`. Null when the type has no real value on any evaluated day.
 */
export function weekAverage(evidence: PersonWeekEvidence, type: PersonSignalType): number | null {
  const days = evaluatedDays(evidence);

  if (type === 'idle_time') {
    const values = days.map(d => valueFor(d, type)).filter((v): v is PersonWeekDayValue => v != null);
    if (!values.length) return null;
    return values.reduce((sum, v) => sum + v.value, 0) / values.length;
  }

  if (!days.some(d => valueFor(d, type) != null)) return null;
  return days.reduce((sum, d) => sum + rawValue(d, type), 0) / days.length;
}

/** Sum over every day including weekend — payroll counts Saturday for heavy_hours, unlike
 * weekAverage's evaluated-only denominator. Null when there's no real per-day data. */
export function weekTotal(evidence: PersonWeekEvidence, type: PersonSignalType): number | null {
  if (!evidence.days.some(d => valueFor(d, type) != null)) return null;
  return evidence.days.reduce((sum, d) => sum + rawValue(d, type), 0);
}

function capitalize(band: CardSeverity): string {
  return band.charAt(0).toUpperCase() + band.slice(1);
}

/**
 * One summary line explaining the card's badge, from its leading (highest-priority) signal.
 * Deliberately the only "why" text this screen shows — the card's own body already lists every
 * fired type in detail, so this doesn't restate them.
 */
export function severityExplainerLine(evidence: PersonWeekEvidence): string {
  const leadingType = evidence.orderedTypes[0];
  const sig = evidence.signals.find(s => s.type === leadingType);
  const final = leadingType ? evidence.severities[leadingType] : undefined;
  if (!leadingType || !sig || !final) return '';
  const base = classifyBaseBand(sig.peak, sig.threshold, sig.x, sig.y, sig.direction ?? 'above');

  // Only true when the cap actually held `final` down to Warning — a range-aggregate fallback's
  // precomputedSeverities can carry a real 'critical' through uncapped, and this must not claim it
  // was capped when it wasn't.
  if (sig.n === 1 && base === 'critical' && final === 'warning') {
    return 'Capped at Warning because only one day went over.';
  }
  if (final !== base) {
    return `Escalated to ${capitalize(final)} because ${sig.n} of ${sig.d} days went over.`;
  }
  const peakLabel = formatPersonSignalValue(leadingType, sig.peak);
  const peakDaysLabel = joinWithAnd(peakWeekdays(evidence, sig));
  return `${capitalize(final)} because ${personSignalTypeLabel(leadingType)} went over on ${sig.n} of ${sig.d} days, `
    + `peaking at ${peakLabel} on ${peakDaysLabel}.`;
}
